import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert, 
  Paper, 
  Divider,
  Grid,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import GoogleMapModal from '../components/GoogleMapModal';
import ImageUpload from '../components/ImageUpload';

const ShopOwnerRequest = ({ language, keywordsData }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [openMap, setOpenMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    phone: user?.user_phone || '',
    email: user?.email || '',
    address: '',
    address_en: '',
    city: '',
    area: '',
    latitude: null,
    longitude: null,
    cuisine_type: '',
    currency: 'MYR',
    delivery_fee: '0',
    min_order_amount: '0',
    delivery_time_min: '30',
    preparation_time_min: '15',
    logo: '',
    cover_image: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const cuisineTypes = [
    'Resturant', 'Pharmacy', 'Super market', 'Car' , 'Home'
  ];

  const currences = [
    'EGP', 'USD', 'EUR', 'MYR' , 'SAR'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, event.target.value) }));
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address || prev.address
    }));
  };

  const handleImageChange = (field) => (imageUrl) => {
    setFormData(prev => ({ ...prev, [field]: imageUrl }));
    // Clear error if it exists
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        if (!value) return language === 'ar' ? 'اسم المتجر مطلوب' : 'Store name is required';
        if (value.length < 3) return language === 'ar' ? 'اسم المتجر يجب أن يكون 3 أحرف على الأقل' : 'Store name must be at least 3 characters';
        return '';
case 'phone':
  if (!value) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
  
  // Remove all spaces, dashes, parentheses, and dots for validation
  const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
  
  // Accept:
  // - Numbers only (7-15 digits)
  // - Numbers with country code starting with + (plus followed by 1-3 digits then 7-12 digits)
  // - Numbers starting with 00 (international format)
  const phoneRegex = /^(\+?[0-9]{1,3})?[0-9]{7,12}$/;
  
  if (!phoneRegex.test(cleanPhone)) {
    return language === 'ar' 
      ? 'الرجاء إدخال رقم هاتف صالح (مثال: 0123456789 أو +20123456789)' 
      : 'Please enter a valid phone number (e.g., 0123456789 or +20123456789)';
  }
  
  // Additional check: ensure total digits are between 7 and 15
  const digitCount = cleanPhone.replace(/\+/g, '').length;
  if (digitCount < 7 || digitCount > 15) {
    return language === 'ar' 
      ? 'رقم الهاتف يجب أن يحتوي على 7-15 رقم' 
      : 'Phone number must contain 7-15 digits';
  }
  
  return '';
      case 'email':
        if (!value) return language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
        return '';
      case 'address':
        if (!value) return language === 'ar' ? 'العنوان مطلوب' : 'Address is required';
        return '';
      case 'city':
        if (!value) return language === 'ar' ? 'المدينة مطلوبة' : 'City is required';
        return '';
      case 'latitude':
        if (!value) return language === 'ar' ? 'يرجى اختيار موقع المتجر على الخريطة' : 'Please select store location on map';
        return '';
      case 'delivery_fee':
        if (value && (value < 0 || value > 100)) {
          return language === 'ar' ? 'رسوم التوصيل يجب أن تكون بين 0 و 100' : 'Delivery fee must be between 0 and 100';
        }
        return '';
      case 'min_order_amount':
        if (value && value < 0) {
          return language === 'ar' ? 'الحد الأدنى للطلب لا يمكن أن يكون سالباً' : 'Minimum order amount cannot be negative';
        }
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newTouched = {};
    const newErrors = {};
    const requiredFields = ['name', 'phone', 'email', 'address', 'city', 'latitude'];
    
    requiredFields.forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    if (Object.values(newErrors).some(error => error)) {
      setMessage({ 
        type: 'error', 
        text: language === 'ar' ? 'يرجى تصحيح الأخطاء الموجودة في النموذج' : 'Please correct the errors in the form' 
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare data for submission
      const submitData = {
        name: formData.name,
        name_en: formData.name_en,
        description: formData.description,
        description_en: formData.description_en,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        address_en: formData.address_en,
        city: formData.city,
        area: formData.area,
        latitude: formData.latitude,
        longitude: formData.longitude,
        cuisine_type: formData.cuisine_type,
        currency : formData.currences,
        delivery_fee: formData.delivery_fee,
        min_order_amount: formData.min_order_amount,
        delivery_time_min: formData.delivery_time_min,
        preparation_time_min: formData.preparation_time_min,
        logo: formData.logo,
        cover_image: formData.cover_image
      };
      
      const response = await api.post('/restaurants_upgrade/request', submitData);
      
      setMessage({ 
        type: 'success', 
        text: language === 'ar' 
          ? 'تم إرسال طلب فتح المتجر بنجاح! سيتم مراجعته من قبل الإدارة قريباً.' 
          : 'Store opening request submitted successfully! It will be reviewed by the admin soon.' 
      });
      
      // Clear form after successful submission
      setTimeout(() => {
        navigate('/account');
      }, 3000);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || (language === 'ar' ? 'فشل إرسال الطلب' : 'Failed to submit request') 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton 
          onClick={() => navigate('/account')}
          sx={{ 
            color: '#FFD700',
            '&:hover': {
              backgroundColor: 'rgba(255, 215, 0, 0.1)'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StorefrontIcon sx={{ fontSize: 48, color: '#FFD700' }} />
        <Typography variant="h3" sx={{ 
          color: '#FFD700',
          fontWeight: 900,
          letterSpacing: '2px'
        }}>
          {language === 'ar' ? 'طلب فتح متجر' : 'Store Opening Request'}
        </Typography>
      </Box>

      {/* Info Message */}
      <Paper sx={{ 
        p: 3, 
        mb: 4,
        backgroundColor: 'rgba(255, 215, 0, 0.05)',
        border: '1px solid #FFD700',
        borderRadius: '16px'
      }}>
        <Typography variant="body1" sx={{ color: '#B3B3B3', textAlign: 'center' }}>
          {language === 'ar' 
            ? 'قم بتعبئة النموذج التالي لطلب فتح متجرك على المنصة. سيتم مراجعة طلبك من قبل الإدارة وسيتم التواصل معك عبر البريد الإلكتروني أو الهاتف.' 
            : 'Fill out the following form to request opening your store on the platform. Your request will be reviewed by the admin and you will be contacted via email or phone.'}
        </Typography>
      </Paper>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ 
            mb: 3,
            backgroundColor: message.type === 'success' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 61, 0, 0.1)',
            color: message.type === 'success' ? '#FFD700' : '#FF3D00',
            border: `1px solid ${message.type === 'success' ? '#FFD700' : '#FF3D00'}`,
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              color: message.type === 'success' ? '#FFD700' : '#FF3D00',
            }
          }}
        >
          {message.text}
        </Alert>
      )}

      {/* Main Form Card */}
      <Paper sx={{ 
        p: 4,
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          border: '1px solid #FFD700',
          boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
        }
      }}>
        <Box component="form" onSubmit={handleSubmit}>
{/* Restaurant Images Section */}
<Typography variant="h6" sx={{ 
  color: '#FFD700',
  fontWeight: 700,
  mb: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 1
}}>
  <StorefrontIcon sx={{ color: '#FFD700' }} /> 
  {language === 'ar' ? 'صور المتجر' : 'Store Images'}
</Typography>

<Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

<Grid container spacing={3}>
  {/* Logo Upload */}
  <Grid item xs={12} md={6}>
    <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
      {language === 'ar' ? 'شعار المتجر' : 'Store Logo'}
    </Typography>
    <ImageUpload
      key="logo-upload" // Add unique key
      value={formData.logo}
      onChange={handleImageChange('logo')}
      productName={formData.name}
      language={language}
      noTitle={true}
    />
  </Grid>

  {/* Cover Image Upload */}
  <Grid item xs={12} md={6}>
    <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
      {language === 'ar' ? 'صورة الغلاف' : 'Cover Image'}
    </Typography>
    <ImageUpload
      key="cover-upload" // Add unique key
      value={formData.cover_image}
      onChange={handleImageChange('cover_image')}
      productName={formData.name}
      language={language}
      noTitle={true}
    />
  </Grid>
</Grid>
          {/* Restaurant Information Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <RestaurantIcon sx={{ color: '#FFD700' }} /> 
            {language === 'ar' ? 'معلومات المتجر' : 'Store Information'}
          </Typography>

          <Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

          {/* Restaurant Name - Arabic */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
              {language === 'ar' ? 'اسم المتجر (بالعربية)' : 'Store Name (Arabic)'} *
            </Typography>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'أدخل اسم المتجر بالعربية' : 'Enter store name in Arabic'}
              value={formData.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RestaurantIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                  '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                }
              }}
            />
          </Box>

          {/* Restaurant Name - English */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
              {language === 'ar' ? 'اسم المتجر (بالإنجليزية)' : 'Store Name (English)'}
            </Typography>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'أدخل اسم المتجر بالإنجليزية' : 'Enter store name in English'}
              value={formData.name_en}
              onChange={handleChange('name_en')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RestaurantIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                  '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                }
              }}
            />
          </Box>

          {/* Description */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'الوصف (بالعربية)' : 'Description (Arabic)'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={language === 'ar' ? 'وصف المتجر بالعربية' : 'Store description in Arabic'}
                value={formData.description}
                onChange={handleChange('description')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'الوصف (بالإنجليزية)' : 'Description (English)'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={language === 'ar' ? 'وصف المتجر بالإنجليزية' : 'Store description in English'}
                value={formData.description_en}
                onChange={handleChange('description_en')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Contact Information Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <PhoneIcon sx={{ color: '#FFD700' }} /> 
            {language === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}
          </Typography>

          <Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'} *
              </Typography>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'أدخل رقم الهاتف' : 'Enter phone number'}
                value={formData.phone}
                onChange={handleChange('phone')}
                onBlur={handleBlur('phone')}
                error={touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder={language === 'ar' ? 'أدخل البريد الإلكتروني' : 'Enter email address'}
                value={formData.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Location Information Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <LocationOnIcon sx={{ color: '#FFD700' }} /> 
            {language === 'ar' ? 'معلومات الموقع' : 'Location Information'}
          </Typography>

          <Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'المدينة' : 'City'} *
              </Typography>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'أدخل المدينة' : 'Enter city'}
                value={formData.city}
                onChange={handleChange('city')}
                onBlur={handleBlur('city')}
                error={touched.city && !!errors.city}
                helperText={touched.city && errors.city}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'المنطقة' : 'Area'}
              </Typography>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'أدخل المنطقة' : 'Enter area'}
                value={formData.area}
                onChange={handleChange('area')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
              {language === 'ar' ? 'العنوان التفصيلي' : 'Detailed Address'} *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={language === 'ar' ? 'أدخل العنوان التفصيلي' : 'Enter detailed address'}
              value={formData.address}
              onChange={handleChange('address')}
              onBlur={handleBlur('address')}
              error={touched.address && !!errors.address}
              helperText={touched.address && errors.address}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                  '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                }
              }}
            />
          </Box>

          {/* Map Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
              {language === 'ar' ? 'موقع المتجر على الخريطة' : 'Store Location on Map'} *
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpenMap(true)}
              startIcon={<MapIcon />}
              sx={{
                py: 1.5,
                borderColor: formData.latitude ? '#4CAF50' : '#FFD700',
                color: formData.latitude ? '#4CAF50' : '#FFD700',
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)'
                }
              }}
            >
              {formData.latitude 
                ? `${language === 'ar' ? 'تم اختيار الموقع' : 'Location Selected'} (${formData.latitude.toFixed(6)}, ${formData.longitude.toFixed(6)})`
                : language === 'ar' ? 'اختر موقع المتجر على الخريطة' : 'Select store location on map'}
            </Button>
            {touched.latitude && errors.latitude && (
              <Typography variant="caption" sx={{ color: '#FF3D00', mt: 1, display: 'block' }}>
                {errors.latitude}
              </Typography>
            )}
          </Box>

          {/* Restaurant Details Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <AccessTimeIcon sx={{ color: '#FFD700' }} /> 
            {language === 'ar' ? 'تفاصيل إضافية' : 'Additional Details'}
          </Typography>

          <Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'نوع المتجر' : 'Store Type'}
              </Typography>
              <TextField
                select
                fullWidth
                value={formData.cuisine_type}
                onChange={handleChange('cuisine_type')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              >
                <MenuItem value="">
                  {language === 'ar' ? 'اختر نوع المتجر' : 'Select store type'}
                </MenuItem>
                {cuisineTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>
                                 <Divider sx={{ backgroundColor: '#2D2D2D', mb: 3 }} />

            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'العملة' : 'Currency'}
              </Typography>
              <TextField
                select
                fullWidth
                value={formData.currency}
                onChange={handleChange('currency')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              >
                <MenuItem value="">
                  {language === 'ar' ? 'اختر نوع العملة' : 'Select currency type'}
                </MenuItem>
                {currences.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'رسوم التوصيل (R.m)' : 'Delivery Fee (R.m)'}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.delivery_fee}
                onChange={handleChange('delivery_fee')}
                onBlur={handleBlur('delivery_fee')}
                error={touched.delivery_fee && !!errors.delivery_fee}
                helperText={touched.delivery_fee && errors.delivery_fee}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'الحد الأدنى للطلب (R.m)' : 'Minimum Order Amount (R.m)'}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.min_order_amount}
                onChange={handleChange('min_order_amount')}
                onBlur={handleBlur('min_order_amount')}
                error={touched.min_order_amount && !!errors.min_order_amount}
                helperText={touched.min_order_amount && errors.min_order_amount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem', mb: 1 }}>
                {language === 'ar' ? 'وقت التوصيل المتوقع (دقائق)' : 'Estimated Delivery Time (minutes)'}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.delivery_time_min}
                onChange={handleChange('delivery_time_min')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Button 
            type="submit" 
            variant="contained" 
            size="large"
            fullWidth
            disabled={loading}
            startIcon={<SendIcon />}
            sx={{ 
              mt: 4,
              py: 1.8,
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              fontSize: '1.1rem',
              borderRadius: '12px',
              border: '1px solid #FFD700',
              '&:hover': {
                backgroundColor: '#FFE55C',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                transform: 'translateY(-2px)'
              },
              '&.Mui-disabled': {
                backgroundColor: '#666',
                color: '#999'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {loading 
              ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') 
              : (language === 'ar' ? 'إرسال الطلب' : 'Submit Request')}
          </Button>
        </Box>
      </Paper>

      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={handleLocationSelect}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />
    </Container>
  );
};

export default ShopOwnerRequest;